import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogFooter, DialogClose } from "components/ui/dialog";
import { Button } from "components/button";
import { toast } from "utils/toast";
import { logError } from "shared/services/ErrorReporting";
import { getSubjectSchemeIdentifier } from "integrations/firebase/collections/products";
import type { ThemaCode } from "reedy-data/api";
import { useSubjects } from "hooks/useSubjects";
import { createManualThemaSubject } from "../../../utils";

interface ModalFooterProps {
  selected: ThemaCode[];
  onClose: () => void;
  onSuccess: () => void;
}

export const ModalFooter = ({
  selected,
  onClose,
  onSuccess,
}: ModalFooterProps) => {
  const { t } = useTranslation(["productDetails", "general"]);
  const { handleAddSubjects } = useSubjects();
  const [isAdding, setIsAdding] = useState(false);

  const getButtonLabel = () => {
    if (isAdding) {
      return t("thema.searchThemaModal.adding");
    }

    return t("thema.searchThemaModal.addButton");
  };

  const handleConfirm = async () => {
    if (selected.length === 0 || isAdding) return;

    setIsAdding(true);
    try {
      // Convert selected Thema-Codes to FirestoreProductSubject
      const subjectsToAdd = selected.map((selectedCode) =>
        createManualThemaSubject(
          selectedCode.code,
          selectedCode.description,
          getSubjectSchemeIdentifier(selectedCode.code),
          t("thema.searchThemaModal.manualReason"),
        ),
      );

      await handleAddSubjects(subjectsToAdd);
      toast.success(
        t("thema.searchThemaModal.successMessage", {
          count: selected.length,
          type: "subject",
        }),
      );

      onSuccess();
      onClose();
    } catch (err) {
      logError(err);
      toast.error(t("productDetails:errorToast.errorUpdate"));
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <DialogFooter className="justify-end space-x-2 flex-shrink-0">
      <DialogClose asChild>
        <Button
          variant="tertiary"
          size="small"
          label={t("general:buttons.cancel")}
          disabled={isAdding}
        />
      </DialogClose>
      <Button
        variant="primary"
        size="small"
        label={getButtonLabel()}
        onClick={handleConfirm}
        disabled={selected.length === 0 || isAdding}
        icon={isAdding ? "loader" : "plus"}
        iconClassName={isAdding ? "animate-loader" : undefined}
        counter={!isAdding ? selected.length : undefined}
        counterTestId="selection-count-badge"
      />
    </DialogFooter>
  );
};
